'use client';
import React, {useCallback, useEffect, useState} from "react";
import {IColor, IReadingPlanTemplate, IScheduleEntry} from "../../core/interfaces";
import Loader from "../../core/components/Loader";
import {Link, useNavigate, useParams} from "react-router-dom";
import {ArrowLeft} from "@mui/icons-material";
import {getReadingPlan, getScheduleEntries, updateGroupColor, updateScheduleEntryDates} from "../../services/readingPlans";
import {Button} from "@mui/material";
import {http} from "../../core/services/http";
import {Pagination} from "../../core/services/utils";
import ColorSelectDropdown from "../../core/components/ColorSelectDropdown";

const EditPlanPage: React.FC = (): JSX.Element => {
    const {iGroupId, planId} = useParams()
    const navigate = useNavigate()

    const [loading, setLoading] = useState<boolean>(false)
    const [plan, setPlan] = useState<IReadingPlanTemplate>()
    const [scheduleEntries, setScheduleEntries] = useState<IScheduleEntry[]>([])
    const [availableColors, setAvailableColors] = useState<IColor[]>([])
    const [editingEntryId, setEditingEntryId] = useState<string | null>(null)
    const [editDates, setEditDates] = useState<{startDate: string, endDate: string}>({startDate: '', endDate: ''})
    const [savingEntry, setSavingEntry] = useState<boolean>(false)
    const [updatingGroupId, setUpdatingGroupId] = useState<string | null>(null)

    const getData = useCallback(async () => {
        try {
            setLoading(true)
            const [planResponse, scheduleResponse, colorsResponse] = await Promise.all([
                getReadingPlan(planId),
                getScheduleEntries(planId),
                http.get<Pagination<IColor>>('/api/dashboard/colors', { params: { page: 0, size: 50 } }),
            ])
            setPlan(planResponse.data)
            setScheduleEntries(scheduleResponse.data)
            setAvailableColors(colorsResponse.data.content)
        } catch (e) {
            console.error(e)
        } finally {
            setLoading(false)
        }
    }, [planId])

    useEffect(() => {
        getData()
    }, [getData])

    const handleStartEditEntry = (entry: IScheduleEntry) => {
        setEditingEntryId(entry.id as string)
        setEditDates({
            startDate: new Date(entry.startDate).toISOString().split('T')[0],
            endDate: new Date(entry.endDate).toISOString().split('T')[0],
        })
    }

    const handleCancelEdit = () => {
        setEditingEntryId(null)
    }

    const handleSaveEntryDates = async (entryId: string) => {
        try {
            setSavingEntry(true)
            const startMs = new Date(editDates.startDate).getTime()
            const endMs = new Date(editDates.endDate).getTime()
            const updated = await updateScheduleEntryDates(entryId, startMs, endMs)
            setScheduleEntries(prev => prev.map(e => e.id === updated.data.id ? updated.data : e))
            setEditingEntryId(null)
        } catch (e) {
            console.error(e)
        } finally {
            setSavingEntry(false)
        }
    }

    const handleGroupColorChange = async (groupId: string, colorId: string) => {
        try {
            setUpdatingGroupId(groupId)
            const updated = await updateGroupColor(groupId, colorId)
            setPlan(prev => {
                if (!prev) return prev
                return {
                    ...prev,
                    readingPlanTemplateGroups: prev.readingPlanTemplateGroups?.map(g =>
                        g.id === updated.data.id ? updated.data : g
                    )
                }
            })
        } catch (e) {
            console.error(e)
        } finally {
            setUpdatingGroupId(null)
        }
    }

    return (
        <div style={{padding: 20, backgroundColor: '#F8F8F8', position: 'relative'}}>
            {loading && <Loader />}

            <div style={{alignItems: 'center', display: 'flex', marginBottom: 20, gap: 12}}>
                <Link to={`/plans/institution-group/${iGroupId}`}>
                    <ArrowLeft style={{backgroundColor: 'black', color: 'white', padding: '0px', width: '20px', height: '20px', textAlign: 'center', lineHeight: '1.1', borderRadius: '10px'}}/>
                </Link>
                <h2 style={{margin: 0}}>Edit Plan</h2>
            </div>

            <div style={{display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 24}}>

                {/* Left panel: Groups */}
                <div>
                    <h4>Reading Groups</h4>
                    {plan?.readingPlanTemplateGroups?.map(group => (
                        <div
                            key={group.id as string}
                            style={{
                                background: '#fff',
                                border: '1px solid #ddd',
                                borderRadius: 8,
                                padding: '12px 16px',
                                marginBottom: 12,
                                borderLeft: `6px solid ${group.color?.code || '#ccc'}`,
                            }}
                        >
                            <div style={{display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 8}}>
                                <strong>
                                    {group.backupGroup ? 'Backup Group' : `Group`}
                                </strong>
                                <ColorSelectDropdown
                                    colors={availableColors}
                                    selectedColorId={group.color?.id as string}
                                    disabled={updatingGroupId === (group.id as string)}
                                    onChange={colorId => handleGroupColorChange(group.id as string, colorId)}
                                />
                            </div>
                            <div style={{fontSize: 13, color: '#555'}}>
                                {group.books?.map(b => (
                                    <div key={b.id as string} style={{padding: '2px 0'}}>• {b.name}</div>
                                ))}
                            </div>
                        </div>
                    ))}
                </div>

                {/* Right panel: Schedule */}
                <div>
                    <h4>Schedule Intervals</h4>
                    {scheduleEntries.length === 0 && !loading && (
                        <p style={{color: '#888'}}>No schedule generated yet.</p>
                    )}
                    {scheduleEntries.map(entry => (
                        <div
                            key={entry.id as string}
                            style={{
                                background: '#fff',
                                border: '1px solid #ddd',
                                borderRadius: 8,
                                padding: '12px 16px',
                                marginBottom: 12,
                            }}
                        >
                            <div style={{display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start'}}>
                                <div>
                                    <strong>Interval #{entry.index}</strong>
                                    {entry.active && (
                                        <span style={{marginLeft: 8, fontSize: 11, background: '#e8f5e9', color: '#388e3c', padding: '2px 6px', borderRadius: 10}}>
                                            Active
                                        </span>
                                    )}
                                </div>
                                <div style={{display: 'flex', gap: 8}}>
                                    {editingEntryId !== (entry.id as string) && (
                                        <>
                                            <Button size="small" variant="outlined" onClick={() => handleStartEditEntry(entry)}>
                                                Edit dates
                                            </Button>
                                            <Button
                                                size="small"
                                                variant="contained"
                                                onClick={() => navigate(`/plans/institution-group/${iGroupId}/${planId}/schedule/${entry.id}`)}
                                            >
                                                View assignments
                                            </Button>
                                        </>
                                    )}
                                </div>
                            </div>

                            {editingEntryId === (entry.id as string) ? (
                                <div style={{marginTop: 8}}>
                                    <div style={{display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 8, marginBottom: 8}}>
                                        <div>
                                            <label style={{fontSize: 12, color: '#666', display: 'block'}}>Start date</label>
                                            <input
                                                type="date"
                                                value={editDates.startDate}
                                                onChange={e => setEditDates(d => ({...d, startDate: e.target.value}))}
                                                style={{width: '100%', padding: '4px 8px', borderRadius: 4, border: '1px solid #ccc'}}
                                            />
                                        </div>
                                        <div>
                                            <label style={{fontSize: 12, color: '#666', display: 'block'}}>End date</label>
                                            <input
                                                type="date"
                                                value={editDates.endDate}
                                                onChange={e => setEditDates(d => ({...d, endDate: e.target.value}))}
                                                style={{width: '100%', padding: '4px 8px', borderRadius: 4, border: '1px solid #ccc'}}
                                            />
                                        </div>
                                    </div>
                                    <div style={{display: 'flex', gap: 8}}>
                                        <Button size="small" variant="contained" disabled={savingEntry} onClick={() => handleSaveEntryDates(entry.id as string)}>
                                            {savingEntry ? 'Saving…' : 'Save'}
                                        </Button>
                                        <Button size="small" variant="outlined" onClick={handleCancelEdit}>
                                            Cancel
                                        </Button>
                                    </div>
                                </div>
                            ) : (
                                <div style={{marginTop: 6, fontSize: 13, color: '#555'}}>
                                    <span>{new Date(entry.startDate).toLocaleDateString()} → {new Date(entry.endDate).toLocaleDateString()}</span>
                                </div>
                            )}
                        </div>
                    ))}
                </div>
            </div>
        </div>
    )
}

export default EditPlanPage
